import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
} from "react";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsiveLine } from "@nivo/line";
import {
  repeatEffect,
  settingsConfig,
  toFormatedDateMMDDHHmm,
} from "utils/commonFunc";
import ApiService from "services/ApiService";
import { defaultChartProps } from "pages/components/CustomDashboardChart";
import { dashboardTheme } from "utils/nivoConfig";
import { useTrafficInOutTab } from "pages/dashboard/RoadTrafficViewD";

const TrafficTotalContext = createContext(null);
const colors = ["var(--pr-50)", "var(--alert-50)"]; //월 평균(파랑), 금일(빨강)

export function SwItsTrafficTotalProvider({ children }) {
  const [currentData, setCurrentData] = useState();

  useEffect(() => {
    return repeatEffect(async () => {
      const response = await ApiService.get(
        "newdashboard/integrated/GetInOutHourTraffic"
      );
      if (response.status === 200) {
        const data = response.data;

        const today = { id: "금일", data: [] };
        const range = { id: "월 평균", data: [] };
        const aggregatedData = {};

        data.rows.forEach((p) => {
          if (!aggregatedData[p.hour]) {
            aggregatedData[p.hour] = { todayValue: 0, rangeValue: 0 };
          }
          aggregatedData[p.hour].todayValue += p.todayValue;
          aggregatedData[p.hour].rangeValue += p.rangeValue;
        });

        Object.keys(aggregatedData).forEach((hour) => {
          today.data.push({
            x: hour,
            y:
              aggregatedData[hour].todayValue === 0
                ? null
                : aggregatedData[hour].todayValue,
          });
          range.data.push({
            x: hour,
            y:
              aggregatedData[hour].rangeValue === 0
                ? null
                : aggregatedData[hour].rangeValue,
          });
        });
        setCurrentData({
          chartData: [today, range],
          updateTime: data.updateTime,
          unit: "대/시",
          todayLabel: `교통량`,
          todayValue: data.totalTodayVolume.toLocaleString(),
          monthValue: data.totalRangeVolume.toLocaleString(),
          changeLabel: "월 평균",
          changeValue: `${data.diffPct.toFixed(2)}`,
          todayIn: data.todayIn ?? 0,
          todayOut: data.todayOut ?? 0,
          rangeIn: data.rangeIn ?? 0,
          rangeOut: data.rangeOut ?? 0,
        });
      }
    }, settingsConfig().contentUpdateDelaySec);
  }, []);

  return (
    <TrafficTotalContext.Provider value={currentData}>
      {children}
    </TrafficTotalContext.Provider>
  );
}

// export function SwItsTrafficTotalInfo({ showMonthValue = false }) {
//     const currentData = useContext(TrafficTotalContext);
//     if (!currentData) return null;

//     const rawValue = showMonthValue ? currentData.monthValue : Math.abs(currentData.changeValue);
//     const changeValueClass = showMonthValue
//         ? parseFloat(currentData.monthValue) >= 0 ? 'minus' : 'plus'
//         : parseFloat(currentData.changeValue) >= 0 ? 'minus' : 'plus';

//     const value = showMonthValue
//         ? rawValue
//         : rawValue;

//     const label = showMonthValue ? '전일 평균' : currentData.changeLabel;

//     return (
//         <div>
//             <div className='flx a_flx_end j_center'><span className='subTit'>수원시</span></div>
//             <div className='flx a_flx_end j_center'>
//                 <span className='nbr'>{currentData.todayValue}</span>
//                 <span className='subTxt'>{currentData.unit}</span>
//             </div>
//             {/* 전월 대비 정보 */}
//             <div className='flx a_center j_center'>
//                 <span className='cmpr' style={{ marginRight: '10px' }}>{label}</span>
//                 <span
//                     className={`cmpr ${changeValueClass}`}
//                     style={{ fontWeight: 'var(--ft-Bold)' }}
//                 >
//                     {parseFloat(currentData.changeValue) >= 0 ? '+ ' : '- '}{value}
//                 </span>
//                 <span className='cmpr' style={{ marginLeft: '10px' }}>
//                     {showMonthValue ? '대' : '%'}
//                 </span>
//             </div>
//         </div>
//     );
// }
export function SwItsTrafficTotalInfo({ showMonthValue = false }) {
  const currentData = useContext(TrafficTotalContext);
  if (!currentData) return null;

  const rawValue = showMonthValue
    ? currentData.monthValue
    : Math.abs(currentData.changeValue);
  const changeValueClass = showMonthValue
    ? parseFloat(currentData.monthValue) >= 0
      ? "minus"
      : "plus" // 이건 혹시 반대로 쓰신 건가요? 넴 ^.^
    : parseFloat(currentData.changeValue) >= 0
    ? "minus"
    : "plus";

  const label = showMonthValue ? "월 평균" : currentData.changeLabel;

  const displayValue = showMonthValue
    ? rawValue
    : `${parseFloat(currentData.changeValue) >= 0 ? "+ " : "- "}${rawValue}`;

  // updateTime에서 시간만 받아오기
  const date = new Date(currentData.updateTime);
  let hours = date.getHours() - 1;
  if (hours < 0) hours = 23;
  hours = String(hours).padStart(2, "0");

  return (
    <div>
      {/* <div className="flx a_flx_end j_center">
        <span className="subTit">수원시</span>
      </div> */}
      <div className="flx a_flx_end j_center">
        <div className="crntTime">
          <div>금일</div>
          <div>{hours}시</div>
        </div>
        <span className="nbr">{currentData.todayValue}</span>
        <span className="subTxt">{currentData.unit}</span>
      </div>
      {/* 전월 대비 정보 */}
      <div className="flx a_center j_center">
        <span className="cmpr" style={{ marginRight: "10px" }}>
          {label}
        </span>
        <span className="cmpr" style={{ marginRight: "10px" }}>
          {hours}시 교통량
        </span>
        <span
          className={`cmpr ${changeValueClass}`}
          style={{ fontWeight: "var(--ft-Bold)" }}
        >
          {displayValue}
        </span>
        <span className="cmpr" style={{ marginLeft: "10px" }}>
          {showMonthValue ? "대" : "%"}
        </span>
      </div>
    </div>
  );
}

export function SwItsTrafficTotalChart() {
  const currentData = useContext(TrafficTotalContext);
  const reversedData = useMemo(() => {
      if (!currentData?.chartData) return [];
      return [...currentData.chartData].reverse(); 
    }, [currentData?.chartData]);
    
  if (!currentData) return null;

  // 데이터 처리 부분은 기존과 동일
  const allYValues = currentData.chartData.flatMap((series) =>
    series.data.map((item) => item.y)
  );
   const maxY = Math.max(...allYValues);
   const minY = Math.min(...allYValues.filter((v) => v != null));

  const roundToNearestThousand = (num) => {
    return Math.round(num / 1000) * 1000;
  };

  const minTickValue = roundToNearestThousand(minY);
  const maxTickValue = roundToNearestThousand(maxY);

  // const generateYTicks = () => {
  //   const step = (maxY - minY) / 4;
  //   return [minY, minY + step, minY + step * 2, minY + step * 3, maxY].map(
  //     (val) => Math.round(val)
  //   );
  // };
  const generateYTicks = () => {
    const step = (maxY - minY) / 4;
    return [minY, minY + step, minY + step * 2, minY + step * 3, maxY].map(
      (val) => roundToNearestThousand(val)
    );
  };

  const formatYTick = (value) => `${value.toLocaleString()} 대`;

  return (
    <div className="traffic-chart" style={{ width: "100%", height: "100%" }}>
      <ResponsiveLine
        {...defaultChartProps}
        theme={{
          ...dashboardTheme,
          grid: {
            line: {
              stroke: "#fff", // 그리드 라인 흰색
              strokeOpacity: 0.2, // 투명도 조절
              strokeWidth: 1,
            },
          },
          axis: {
            ticks: {
              text: {
                fill: "#fff", // 축 텍스트 흰색
                fontSize: "var(--ft-26)",
                fontWeight: "var(--ft-Light)",
              },
            },
          },
          legends: {
            text: {
              fontSize: "var(--ft-26)",
              fontWeight: "var(--ft-Light)",
            },
          },
        }}
        // data={currentData.chartData.reverse()}
        data={reversedData}
        legends={[
          {
            anchor: "bottom",
            direction: "row",
            translateY: 26,
            itemDirection: "left-to-right",
            itemWidth: 120,
            itemHeight: 0,
            itemTextColor: "#FFFFFF",
            symbolSize: 25,
            fontSize: "var(--ft-26)",
          },
        ]}
        lineWidth={4}
        colors={colors}
        margin={{ top: 10, right: 10, bottom: 50, left: 100 }}
        xScale={{ type: "point" }}
        yScale={{
          type: "linear",
          min: minY,
          max: maxY,
        }}
        axisLeft={{
          tickValues: [minTickValue, maxTickValue], // 최소 & 최대만 표시
          format: (value) => (value / 1000).toLocaleString(),
          tickSize: 5,
          tickPadding: 10,
          tickRotation: 0,
          tickColor: "#fff",
        }}
        
        axisBottom={null} //
        enableGridX={false} // x축 그리드 비활성화
        enableGridY={true} // y축 그리드 활성화
        gridYValues={generateYTicks()} // y축 그리드 위치 지정
        enableArea={true}
        areaBaselineValue={minY}
        areaOpacity={0.1}
        enablePoints={false}
        isInteractive={true}
        useMesh={true}
        tooltip={({ point }) => (
          <div
            style={{
              background: "#333",
              padding: "12px",
              color: "white",
              fontSize: "14px",
              borderRadius: "4px",
            }}
          >
            <div>시간: {point.data.x}시</div>
            <div>교통량: {point.data.y.toLocaleString()} 대</div>
          </div>
        )}
      />
    </div>
  );
}

const CustomRangeDotAndLineLayer = ({ bars, data }) => {
  return (
    <>
      {bars.map((bar) => {
        // indexValue로 원본 데이터에서 해당 시간의 데이터 찾기
        const hourData = data.find((d) => d.hour === bar.data.indexValue);

        if (!hourData || !hourData.rangeValue || hourData.rangeValue <= 0)
          return null;

        // bar.data.value는 'today' 값임
        const ratio = hourData.rangeValue / hourData.today;

        // bar.y는 바의 상단 위치, bar.height는 바의 높이
        const dotY = bar.y + bar.height * (1 - ratio);

        return (
          <g key={`dotline-${bar.key}`}>
            {/* 수평선 (바의 좌우로 약간 확장) */}
            <line
              x1={bar.x}
              y1={dotY}
              x2={bar.x + bar.width}
              y2={dotY}
              stroke="#FFD700"
              strokeWidth={5}
            />

            {/* 중앙 점 */}
            <circle
              cx={bar.x + bar.width / 2}
              cy={dotY}
              r={5}
              fill="#FFD700"
              stroke="#FFD700"
              strokeWidth={2}
            />
          </g>
        );
      })}
    </>
  );
};
// 라인차트 레이어
const CustomLineLayer = ({ bars, data }) => {
  const path = useMemo(() => {
    const points = bars.map((bar) => {
      const x = bar.x + bar.width / 2;
      const y = bar.y;
      return [x, y];
    });

    if (points.length < 2) return null;

    const d = points.reduce(
      (acc, [x, y], idx) => acc + `${idx === 0 ? "M" : "L"}${x},${y} `,
      ""
    );

    return d.trim();
  }, [bars]);

  return (
    <g>
      <path
        d={path}
        fill="none"
        stroke="#FFD700" // 노란색 라인
        strokeWidth={3}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  );
};

function transformChartData(chartData) {
  const result = [];
  for (let i = 0; i < 24; i++) {
    const hour = i.toString();
    const todayData =
      chartData.find((series) => series.id === "금일")?.data[i]?.y ?? null;
    const rangeData =
      chartData.find((series) => series.id === "전일")?.data[i]?.y ?? null;

    result.push({
      hour,
      today: todayData,
      rangeValue: rangeData,
    });
  }

  return result;
}


export function SwItsTrafficTotalBarChart() {
  const currentData = useContext(TrafficTotalContext);
  const processedData = useMemo(() => {
    if (!currentData || !currentData.chartData) return [];
  
    const hourlyData = {};
  
    currentData.chartData.forEach((series) => {
      const isToday = series.id === "금일"; // id 보고 오늘 데이터인지 구분
      series.data.forEach((point) => {
        const hour = point.x;
        const value = point.y ?? 0;
  
        if (!hourlyData[hour]) {
          hourlyData[hour] = {
            hour: `${hour}`,
            today: 0,
            rangeValue: 0,
          };
        }
  
        if (isToday) {
          hourlyData[hour].today = value;
        } else {
          hourlyData[hour].rangeValue = value;
        }
      });
    });
  
    return Object.values(hourlyData).sort(
      (a, b) => parseInt(a.hour) - parseInt(b.hour)
    );
  }, [currentData]);

  const filteredData = useMemo(() => {
    return processedData.filter((item) => item.today > 0);
  }, [processedData]);

  const { minY, maxY } = useMemo(() => {
    const allY = filteredData.flatMap((d) =>
      [d.today, d.rangeValue].filter((val) => val > 0)
    );

    const min = 0;
    const max = allY.length ? Math.max(...allY) * 1.2 : 100;

    return { minY: min, maxY: max };
  }, [filteredData]);

  const yTicks = useMemo(() => {
    const range = maxY || 100;
    const step = range / 4;
    return Array.from({ length: 5 }, (_, i) => Math.round(i * step));
  }, [maxY]);

  if (!filteredData.length) return <div>데이터가 없습니다.</div>;

  return (
    <div style={{ width: "100%", height: "620px" }}>
      <ResponsiveBar
        data={filteredData}
        keys={["today"]}
        indexBy="hour"
        padding={0.3}
        colors={["var(--pr-50)", "#1E90FF"]}
        valueScale={{ type: "linear", min: minY, max: maxY }}
        margin={{ top: 60, right: 30, bottom: 50, left: 100 }}
        axisLeft={{
          tickValues: yTicks,
          // format: (value) =>
          //   `${(Math.round(value / 10000) * 10000).toLocaleString()} 대`,
          format: (value) => `${Math.round(value / 10000)}만`,
          tickSize: 5,
          tickPadding: 10,
          tickRotation: 0,
          tickColor: "#fff",
        }}
        axisBottom={{
          tickSize: 0,
          tickPadding: 10,
          tickColor: "#fff",
          format: (value) => `${value}시`,
          legend: "시간대",
          legendPosition: "middle",
          legendOffset: 40,
        }}
        enableGridY={true}
        gridYValues={yTicks}
        labelSkipWidth={999}
        theme={{
          axis: {
            ticks: {
              text: {
                fill: "#fff",
                fontSize: 25,
              },
            },
          },
          grid: {
            line: {
              stroke: "#fff",
              strokeOpacity: 0.2,
            },
          },
          legends: {
            text: {
              fill: "#fff",
              fontSize: 25,
              fontWeight: 600,
            },
          },
        }}
        tooltip={({ id, value, indexValue, data }) => (
          <div
            style={{
              background: "#333",
              padding: "12px",
              color: "white",
              fontSize: "14px",
              borderRadius: "4px",
            }}
          >
            <div>{indexValue}시</div>
            <div>오늘: {value.toLocaleString()} 대</div>
            {data.rangeValue > 0 && (
              <div>월 평균: {data.rangeValue.toLocaleString()} 대</div>
            )}
          </div>
        )}
        legends={[
          {
            dataFrom: "keys",
            anchor: "top-right",
            direction: "row",
            translateY: -40,
            itemsSpacing: 10,
            itemWidth: 80,
            itemHeight: 20,
            itemDirection: "left-to-right",
            symbolSize: 12,
            symbolShape: "square",
            effects: [
              {
                on: "hover",
                style: {
                  itemTextColor: "#fff",
                },
              },
            ],
            data: [
              { id: "today", label: "오늘", color: "var(--pr-50)" },
              { id: "range", label: "월 평균", color: "#FFD700" },
            ],
          },
        ]}
        layers={[
          "grid",
          "axes",
          "bars",
          ({ bars }) => (
            <CustomRangeDotAndLineLayer bars={bars} data={filteredData} />
          ),
          "markers",
          "legends",
        ]}
        borderRadius={2}
        isInteractive={true}
        animate={true}
      />
    </div>
  );
}
